/**
 * <RequireAuth> — guards every protected route.
 *
 * Behavior:
 *   - While the session query is loading we render a Skeleton — keeps the
 *     shell visible so the page doesn't flash to /login on every reload.
 *   - On unauthenticated state (no access token — incl. the fetcher's hard-401
 *     reset — or no profile at all after the first load) we redirect to
 *     /login. A FAILED BACKGROUND REFETCH with a profile still in cache does
 *     NOT redirect (В-О2, фикс-пак 1 — see the comment at the branch), preserving the original URL on `state.from`
 *     so a future enhancement can return the user to where they came
 *     from after login.
 *   - On `mustChangePassword === true` we redirect to /force-password-change
 *     (V3-FIX-NEW-5). The exclusion-path check is what stops a redirect
 *     loop — RequireAuth must NOT bounce a user already on the page back
 *     to it. The page itself enforces the inverse guard (clears the user
 *     to "/" when the flag is false) so deep-links can't sit on a useless
 *     page after the flag clears in another tab.
 *   - Блок 2 · Ф8 · В-О2: on every protected-route PATH change the session
 *     (`/auth/me`, права человека) is re-requested immediately (Р-198 —
 *     «отзыв действует со следующего перехода»). See `useSessionFreshness`.
 */
import * as React from 'react';
import { Navigate, Outlet, useLocation } from 'react-router-dom';

import { Skeleton } from '@/components/ui/skeleton';
import { getAccess } from '@/lib/fetcher';
import { isSessionLost, useMe } from '@/hooks/use-me';
import { useSessionFreshness } from '@/hooks/use-session-freshness';

export interface RequireAuthProps {
  children?: React.ReactNode;
}

export function RequireAuth({ children }: RequireAuthProps): React.ReactElement {
  const location = useLocation();
  const hasAccess = getAccess() !== null;
  const { data: me, isLoading, isError } = useMe();
  // Блок 2 · Ф8 · В-О2 (Р-198): при переходе между страницами профиль — а с
  // ним права — перезапрашивается сразу. Стоит ЗДЕСЬ, потому что эта оболочка
  // смонтирована на всю сессию и видит каждый переход; разбор — в самом хуке.
  useSessionFreshness(location.pathname, me);

  // No access token at all → straight to login (skip the loading flash).
  if (!hasAccess) {
    return <Navigate to="/login" replace state={{ from: location.pathname }} />;
  }

  if (isLoading) {
    return (
      <div data-testid="require-auth-loading" className="space-y-3 p-6">
        <Skeleton className="h-6 w-1/3" />
        <Skeleton className="h-32 w-full" />
      </div>
    );
  }

  // ⚠️ НА ВХОД УВОДИТ ОТСУТСТВИЕ ПРОФИЛЯ, А НЕ ОШИБКА ЗАПРОСА (Ф8 · В-О2,
  // фикс-пак 1). Стояло `isError || !me` — и до волны это было почти
  // безвредно: профиль перезапрашивался редко. Теперь он перезапрашивается на
  // КАЖДОМ переходе (`useSessionFreshness`), и любой сбой этого запроса при
  // живой сессии — обрыв сети, 502 от прокси, 429 от ограничителя — выбрасывал
  // бы человека на вход посреди работы.
  //
  // При ошибке ФОНОВОГО перезапроса клиент запросов данные не стирает:
  // `me` остаётся прежним, и человек продолжает работать с прежним профилем;
  // следующий переход попробует снова. Права на сервере при этом читаются из
  // базы на каждом запросе — прежний профиль в вебе ничего не открывает.
  //
  // Что по-прежнему уводит на вход:
  //   · профиля нет вовсе (первая загрузка завершилась отказом) — `!me`;
  //   · настоящий 401: `lib/fetcher.ts` сбрасывает токен (и сам уводит
  //     страницу жёстким переходом). Вторая половина условия — тот же исход
  //     без расчёта на жёсткий переход: токена нет, а запрос профиля
  //     завершился ошибкой. ⚠️ Чтение `isError` здесь НЕСУЩЕЕ и по другой
  //     причине: клиент запросов перерисовывает компонент только по тем полям
  //     результата, которые тот читал; без него отказ запроса при неизменных
  //     данных эту оболочку не перерисовал бы вовсе.
  //
  // Само правило — `isSessionLost` (`hooks/use-me.ts`), общее для всех стражей.
  // `|| !me` — только ради сужения типа: правило его уже включает.
  if (isSessionLost({ me, isError }) || !me) {
    return <Navigate to="/login" replace state={{ from: location.pathname }} />;
  }

  // mustChangePassword interstitial — dedicated /force-password-change
  // route (V3-FIX-NEW-5). The path-equality check prevents the
  // user-is-on-the-page-already redirect loop.
  if (me.mustChangePassword && location.pathname !== '/force-password-change') {
    return <Navigate to="/force-password-change" replace />;
  }

  return <>{children ?? <Outlet />}</>;
}
